/* Karaoke Studio — put the user's own lyrics ON the singing. For real.
 *
 * WHY THIS EXISTS
 * Until 2026-09-18 pasted lyrics were laid out by KaraokeLyrics.fromPlainText:
 * the song's duration divided by the number of lines. It was rendered with the
 * same highlight, count-in, fullscreen and video burn-in as a real synced file,
 * and nothing on the page said the times were invented. Measured on songs with
 * HUMAN line annotations (8 × jamendolyrics, 8 × Russian songs against their
 * LRCLIB stamps): the median line sat 10–26 s away from where it is sung, 4–8 %
 * of lines within a second. Feedback #1704, a vocalist: «не совпадает с
 * мелодией, не попадает в ритм» — a precise description.
 *
 * Nothing cheap fixes it. Spreading lines over the loud parts of the vocal stem
 * gets 19 % within a second; matching lines to sung phrases by length gets 19 %
 * too, with a WORSE median than the even spread. Two phrases of equal length
 * are indistinguishable to energy. So there is no "draft" lane here: a line is
 * either placed by the aligner, tapped by the user, or shown without a time.
 *
 * WHAT IT DOES
 * Forced alignment — Viterbi over a CTC model's log-probabilities for the one
 * path that spells exactly the user's text (ctc-forced-align.js). Two providers:
 *   gigaam  Russian (MIT; its vocabulary is Cyrillic letters)
 *   phon    every other language espeak-ng can read (en de es fr pt it nl pl
 *           tr id uk ko ar): facebook/wav2vec2-xlsr-53-espeak-cv-ft, Apache-2.0,
 *           which hears PHONEMES; the words reach it as espeak-ng IPA from our
 *           server (/api/lyrics-g2p). It replaced MMS (CC-BY-NC-4.0) 24.09.2026.
 * /karaoke adds the two things it uniquely owns:
 *
 *   input  stem + 0.25·mix   one pass; keeps what separation lost in processed
 *                            choruses, without paying for two model runs
 *   gate   VocalActivity     the aligner cannot put a word where nobody sings
 *
 * Line starts within 0.3 s, 79 JamendoLyrics songs with human annotation
 * (en de es fr), tests/scratch/jamendo, 24.09.2026:
 *                                          MMS (before)   phon
 *   the mix                    ← /audio-to-video   83.8 %      83.1 %
 *   stem + 0.25·mix, gated     ← here              87.2 %      94.1 %
 * (the stem there is htdemucs; the best per-language letter models measured
 * 82.7–95.8 % on the same stems, below phon in every language)
 *
 * WHAT IT REFUSES
 *   · no singing in the file (a backing track): the vocal stem is quieter than
 *     −15 dB against the mix in all but a few seconds. The aligner would still
 *     return a confident-looking path (score −0.23 on a karaoke instrumental),
 *     so the refusal is decided on the STEM, before any model is fetched;
 *   · a script nothing here can read (CJK and the like): said up front.
 * And it WARNS when the words do not look like what is sung: the median
 * per-word confidence, with a threshold per lane set for few false alarms
 * (TEXT_MISMATCH_CONF* below); the whole-path score does not separate right
 * text from another song's (−0.15…−0.69 against −0.33…−1.6 — they overlap).
 *
 * Pure helpers are exported for tests (tests/js/karaoke-autosync.test.mjs).
 */
(function (root, factory) {
    var api = factory(root);
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.KaraokeAutoSync = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function (root) {
    'use strict';

    var SR = 16000;
    var BLEND = 0.25;                 // share of the mix added back to the stem
    var VOCAL_REL_DB = -15;           // a window "has a voice" above this, stem vs mix
    var VOCAL_MIN_SHARE = 0.20;       // songs measure 0.49–0.96, a backing track 0.02
    /* «These words do not look like what is sung»: the median per-word
       confidence below a threshold. A WARNING, and a false one costs more than
       a miss — it tells a person holding the right lyrics that they are wrong,
       while wrong lyrics show themselves on the first play anyway. So each
       threshold is set for few false alarms. Measured on 79 songs, each also
       run with the NEXT song's lyrics (tests/scratch/jamendo/conf_calib.py,
       gop_calib-scratch.py — a relative score, path minus the frames' best,
       separated no better where it matters: AUC 0.986 against 0.981 on the stem):
         GigaAM       right −0.0…−1.3, wrong −7.8…−12.9 → −3.0, no overlap;
         phon, stem   −4.0: 1 right in 79 flagged, 67 of 79 wrong caught
                      (−3.0 flagged 8 right — 10 %);
         phon, mix    −4.6: 4 right flagged, 41 wrong caught — the mix is a weak
                      detector at any threshold (−3.6 flagged 14 right, 18 %).
       In the real page (q4f16, Kim/MUSION stems, 24.09.2026) right lyrics sit
       LOWER on the mix than in the Python bench (median −3.21 against −2.43):
       −3.6 there flagged 4 of 16 right songs, −4.6 none. */
    var TEXT_MISMATCH_CONF = -3.0;        // GigaAM, both lanes
    var TEXT_MISMATCH_CONF_STEM = -4.0;   // phon on the separated voice
    var TEXT_MISMATCH_CONF_MIX = -4.6;    // phon on the plain mix

    /* Languages the phoneme lane takes — the ones our server has an espeak-ng
       voice for (config/lyrics-align.php `voices`, minus Russian, which stays on
       GigaAM). LyricsAlignLanguagesTest keeps the two lists in step. */
    var PHON_LANGS = ['en', 'de', 'es', 'fr', 'pt', 'it', 'nl', 'pl', 'tr', 'id', 'uk', 'ko', 'ar'];
    var G2P_URL = '/api/lyrics-g2p';

    /* ── Which alphabet is the text in ─────────────────────────────────────── */
    function scriptOf(text) {
        var s = String(text || ''), cyr = 0, lat = 0, other = 0;
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            if ((c >= 0x0400 && c <= 0x04FF)) cyr++;
            else if ((c >= 0x41 && c <= 0x5A) || (c >= 0x61 && c <= 0x7A) || (c >= 0xC0 && c <= 0x24F)) lat++;
            else if (c > 0x24F && /\p{L}/u.test(s[i])) other++;
        }
        var total = cyr + lat + other;
        if (!total) return { script: 'none', cyr: 0, lat: 0, other: 0, ukrainian: false };
        var script = (other / total > 0.5) ? 'other' : (cyr >= lat ? 'cyrillic' : 'latin');
        /* Ukrainian is the language, not «an і somewhere»: the site's one detector
           (timbrica-core). A Russian song naming «Київ» stays on GigaAM — its
           model text drops the і, the word still has its place. */
        var cl = root && root.__cyrillicLang;
        var ukrainian = cyr > 0 && typeof cl === 'function' && cl(s) === 'uk';
        return { script: script, cyr: cyr, lat: lat, other: other, ukrainian: ukrainian };
    }

    /* GigaAM is a RUSSIAN model: Ukrainian і/ї/є are not in its vocabulary, so
       Ukrainian goes to the phoneme lane with everything else. Russian stays on
       GigaAM — it runs on any CPU at better than real time; the phoneme model,
       like any wav2vec2-large, needs a GPU to be bearable (see the worker).
       The LANGUAGE (which espeak voice reads the words) comes from the words
       themselves — LyricsLang: function words vote, the page locale only breaks
       a tie. A Russian singer in English is the normal case, not an edge. */
    function planFor(text, hint) {
        var sc = scriptOf(text);
        if (sc.script === 'none') return { provider: null, reason: 'no-text', script: sc.script };
        if (sc.script === 'cyrillic' && !sc.ukrainian) return { provider: 'gigaam', script: sc.script, lang: 'ru' };
        var LL = root && root.LyricsLang;
        var d = LL ? LL.detect(String(text || '').split('\n'), { hint: hint }) : null;
        var lang = d ? d.lang
            : (sc.script === 'cyrillic' ? 'uk' : (sc.script === 'latin' ? String(hint || 'en').slice(0, 2) : null));
        var script = d ? d.script : sc.script;
        if (!lang || PHON_LANGS.indexOf(lang) < 0) return { provider: null, reason: 'script-unsupported', script: script };
        return { provider: 'phon', script: script, lang: lang, perLine: d ? d.perLine : null,
                 confident: d ? !!d.confident : false };
    }

    /* ── The text the MODEL sees, word for word ────────────────────────────── */
    function modelWord(word, provider) {
        var w = String(word || '').toLowerCase();
        if (provider === 'phon') {
            /* The word goes to espeak-ng as written — accents, apostrophes and
               digits are pronunciation ("2" is sung "two"); only the punctuation
               AROUND it goes. A word with no letter or digit (—, 🎤, ♪) is not
               sung and is not sent. Lower case: an all-caps shout is a word, not
               an acronym to spell out letter by letter. */
            w = w.normalize('NFC').replace(/[’`´]/g, "'");
            if (!/[\p{L}\p{N}]/u.test(w)) return '';
            return w.replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^\p{L}\p{N}]+$/u, '');
        }
        // GigaAM: its vocabulary carries Cyrillic, Latin and digits; punctuation
        // is a TOKEN there (an end-to-end model writes commas), and forcing the
        // lyric sheet's commas onto the singing costs the path for nothing.
        return w.replace(/ё/g, 'е').replace(/[^0-9a-z\u0430-\u044F]+/g, '');
    }

    /* lines → { text, perLine:[[{display, idx}]] } where idx is the word's index
       in the model text, or -1 when nothing of it survives (an emoji, "—", "x2").
       Such a word keeps its place on screen and gets its time from neighbours;
       dropping it from the MODEL text is what keeps every later index honest. */
    function prepareWords(lines, provider, plan) {
        var flat = [], perLine = [], langs = [];
        for (var i = 0; i < (lines || []).length; i++) {
            var toks = String(lines[i] == null ? '' : lines[i]).trim().split(/\s+/).filter(Boolean);
            var row = [];
            /* the language that READS this line (a phoneme model hears sounds, so
               an English chorus in a German song is phonemized in English) */
            var lang = (plan && plan.perLine && plan.perLine[i]) || (plan && plan.lang) || null;
            for (var t = 0; t < toks.length; t++) {
                var m = modelWord(toks[t], provider);
                if (m) { row.push({ display: toks[t], idx: flat.length }); flat.push(m); langs.push(lang); }
                else row.push({ display: toks[t], idx: -1 });
            }
            perLine.push(row);
        }
        return { text: flat.join(' '), count: flat.length, perLine: perLine, words: flat, langs: langs };
    }

    /* ── Is anyone singing in this file? ───────────────────────────────────── */
    function vocalShare(vocal, mix, sr) {
        var win = Math.round(sr), n = Math.min(vocal.length, mix.length), live = 0, hit = 0;
        for (var t = 0; t + win <= n; t += win) {
            var a = 0, b = 0;
            for (var i = t; i < t + win; i++) { a += vocal[i] * vocal[i]; b += mix[i] * mix[i]; }
            var mr = Math.sqrt(b / win), vr = Math.sqrt(a / win);
            if (20 * Math.log10(mr + 1e-12) < -40) continue;          // digital silence is not evidence
            live++;
            if (20 * Math.log10((vr + 1e-12) / (mr + 1e-12)) > VOCAL_REL_DB) hit++;
        }
        return live ? hit / live : 0;
    }

    /* ── Aligner words → the KaraokeLyrics timeline (with per-word stamps) ─── */
    function timelineFromWords(lines, prepared, words, durationS) {
        var out = [], confs = [];
        for (var l = 0; l < prepared.perLine.length; l++) {
            var row = prepared.perLine[l];
            if (!row.length) continue;
            var ws = [], cSum = 0, cN = 0;
            for (var k = 0; k < row.length; k++) {
                var w = row[k].idx >= 0 ? words[row[k].idx] : null;
                ws.push({ startS: w ? Math.max(0, w.start) : null, endS: w ? w.end : null, text: row[k].display });
                if (w && typeof w.conf === 'number') { cSum += w.conf; cN++; confs.push(w.conf); }
            }
            out.push({ text: String(lines[l]).trim().replace(/\s+/g, ' '), words: ws, conf: cN ? cSum / cN : null });
        }
        // Words the model never saw take their time from the neighbours, across
        // line borders if need be — order on screen is preserved by construction.
        var flat = [];
        out.forEach(function (ln) { ln.words.forEach(function (w) { flat.push(w); }); });
        for (var i = 0; i < flat.length; i++) {
            if (flat[i].startS != null) continue;
            var a = i - 1; while (a >= 0 && flat[a].startS == null) a--;
            var b = i + 1; while (b < flat.length && flat[b].startS == null) b++;
            var lo = a >= 0 ? (flat[a].endS != null ? flat[a].endS : flat[a].startS) : 0;
            var hi = b < flat.length ? flat[b].startS : lo;
            flat[i].startS = lo + (hi - lo) * ((i - a) / (b - a));
        }
        for (var m = 1; m < flat.length; m++) if (flat[m].startS < flat[m - 1].startS) flat[m].startS = flat[m - 1].startS;
        var timeline = out.map(function (ln) {
            return { startS: ln.words[0].startS, endS: 0, text: ln.text, conf: ln.conf,
                     words: ln.words.map(function (w) { return { startS: w.startS, text: w.text + ' ' }; }) };
        });
        for (var q = 0; q < timeline.length; q++) {
            // Same shape parseLRC produces: a line ends where the next begins. Word
            // texts carry their trailing space because the painters join('') them.
            var last = timeline[q].words[timeline[q].words.length - 1];
            last.text = last.text.replace(/\s+$/, '');
            timeline[q].endS = (q + 1 < timeline.length)
                ? Math.max(timeline[q].startS + 0.3, timeline[q + 1].startS)
                : Math.min(durationS || 1e9, timeline[q].startS + 6);
        }
        confs.sort(function (x, y) { return x - y; });
        return { timeline: timeline, medianConf: confs.length ? confs[confs.length >> 1] : null };
    }

    /* ── Browser side ──────────────────────────────────────────────────────── */
    function toMono16k(bufferA, bufferB, gainB) {
        var OAC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
        if (!OAC) return Promise.reject(new Error('no-offline-audio-context'));
        var frames = Math.max(1, Math.ceil(bufferA.duration * SR));
        var oac = new OAC(1, frames, SR);
        [[bufferA, 1], [bufferB, gainB]].forEach(function (p) {
            if (!p[0] || !p[1]) return;
            var src = oac.createBufferSource(), g = oac.createGain();
            src.buffer = p[0]; g.gain.value = p[1];
            src.connect(g); g.connect(oac.destination); src.start(0);
        });
        return oac.startRendering().then(function (r) { return r.getChannelData(0).slice(); });
    }

    /* `gpu` names the file this device will load: the phoneme model is two
       files (q4f16 for WebGPU, q4 for WASM), and a cached GPU copy says nothing
       about a run that has to take the WASM one — "0 MB" would be a lie. */
    function modelCached(provider, gpu) {
        if (typeof caches === 'undefined') return Promise.resolve(false);
        var name = provider === 'gigaam' ? 'gigaam-model-v3' : 'transformers-cache';
        var re = provider === 'gigaam' ? /gigaam_v3_e2e_ctc_int8\.onnx/
            : (gpu ? /wav2vec2-xlsr-53-espeak-cv-ft\/.*\/model_q4f16\.onnx/ : /wav2vec2-xlsr-53-espeak-cv-ft\/.*\/model_q4\.onnx/);
        return caches.open(name).then(function (c) { return c.keys(); }).then(function (keys) {
            for (var i = 0; i < keys.length; i++) if (keys[i] && re.test(keys[i].url)) return true;
            return false;
        }).catch(function () { return false; });
    }

    /* What the person is agreeing to BEFORE the first byte moves. Sizes are the
       files on the wire, in MiB like every other size on the site: GigaAM int8
       319 869 121 B (HF Content-Length, 2026-09-18); the phoneme model, our own
       export on our own origin, q4f16 196 713 881 B and q4 241 493 665 B
       (24.09.2026). ModelSizeClaimsTest holds the copy to these numbers.
       Speeds: GigaAM 2.7× faster than real time on one WASM thread. The phoneme
       model, measured end to end in the real page 24.09.2026 (39 runs, songs of
       3–4.5 min, RTX 4060, the model already cached — so the time is worker
       start, session and shader compile, the known-answer check, the server's
       phonemes and the inference): 0.17–0.54× of the song on WebGPU, median
       0.28×; the inference alone is ~0.06×, the rest a fixed ~30–40 s. On WASM
       a 46 s clip took 6.4× under load (3.8× measured idle for the same size
       of model). The promise is made to the median, not to the best run. */
    var MODEL_MB = { gigaam: 305, phonGpu: 188, phonWasm: 230 };
    function estimate(provider, durationS, hasGpu) {
        if (provider === 'gigaam') return { downloadMB: MODEL_MB.gigaam, seconds: Math.round(durationS / 2.2) + 10, gpu: false };
        if (hasGpu) return { downloadMB: MODEL_MB.phonGpu, seconds: Math.round(durationS * 0.12) + 30, gpu: true };
        return { downloadMB: MODEL_MB.phonWasm, seconds: Math.round(durationS * 4) + 40, gpu: false };
    }

    /* A new model is a new verdict: a GPU that passed or failed MMS's check says
       nothing about this model's kernels. */
    var GPU_VERDICT_KEY = 'tmb_phon_align_gpu_v1';
    function gpuRejected() {
        try {
            var v = JSON.parse(localStorage.getItem(GPU_VERDICT_KEY) || 'null');
            return !!(v && v.ok === false && (Date.now() - v.at) < 30 * 24 * 3600 * 1000);
        } catch (_) { return false; }
    }
    function rememberGpu(ok, why) {
        try { localStorage.setItem(GPU_VERDICT_KEY, JSON.stringify({ ok: !!ok, why: why || '', at: Date.now() })); } catch (_) {}
    }
    function hasWebGpu() { return !!(root.navigator && root.navigator.gpu) && !gpuRejected(); }

    /* The GPU rung is for a REAL adapter. A software one (SwiftShader, WARP on a
       remote desktop — `isFallbackAdapter`) «has WebGPU», passes the known answer
       and runs a wav2vec2-large slower than the WASM rung, while the consent had
       promised GPU seconds (found 24.09.2026: headless Chromium, "about 20 s").
       The adapter's identity has ONE owner, OrtEpVerify.adapterIdentity() — the
       answer the separator's support() reads too (musion-stem-separator.js). */
    var _gpuUsableP = null;
    function needVerifier() {
        if (root.OrtEpVerify) return Promise.resolve();
        if (!root.document) return Promise.reject(new Error('no-document'));
        return new Promise(function (resolve, reject) {
            var el = root.document.createElement('script');
            el.src = root.__jsSrc ? root.__jsSrc('/js/ort-ep-verify.js') : '/js/ort-ep-verify.js';
            el.onload = function () { if (root.OrtEpVerify) resolve(); else reject(new Error('ort-ep-verify: no global')); };
            el.onerror = function () { reject(new Error('ort-ep-verify failed to load')); };
            root.document.head.appendChild(el);
        });
    }
    /* Deadlines: the ADAPTER query gets 5 s — a driver that never answers
       requestAdapter() is not a usable GPU and must not hold the consent. The
       verifier SCRIPT is loaded by the page up front and only fetched here as a
       fallback, with a generous 20 s: measured 24.09.2026, a busy server made
       that fetch alone exceed 5 s, and a real RTX 4060 was sent to the WASM lane
       (4× the song instead of 0.12×). An answer produced by a deadline is not
       kept — the next call asks again. */
    function within(p, ms) {
        return Promise.race([p, new Promise(function (resolve) { setTimeout(function () { resolve('timeout'); }, ms); })]);
    }
    function gpuUsable() {
        if (!hasWebGpu()) return Promise.resolve(false);
        if (!_gpuUsableP) {
            _gpuUsableP = within(needVerifier().then(function () { return true; }, function () { return false; }), 20000)
                .then(function (loaded) {
                    if (loaded !== true) return loaded;          // false (failed) or 'timeout'
                    return within(Promise.resolve(root.OrtEpVerify.adapterIdentity()), 5000);
                }).then(function (id) {
                    if (id === 'timeout') { _gpuUsableP = null; return false; }
                    return !!(id && id !== true && id.present && !id.fallback);
                }, function () { return false; });
        }
        return _gpuUsableP.then(function (ok) { return ok && hasWebGpu(); });
    }

    /* The known-answer clip for the GPU rung: three of the site's own spoken
       digits, so the expected word windows are the clips' own lengths. Their
       phonemes are fixed here (espeak-ng 1.51 en-us, stress removed) — the check
       must not depend on the server it would be checking the GPU for. */
    var KAT_IPA = ['θɹiː', 'sɛvən', 'naɪn'];
    function buildKat(base) {
        var names = ['3', '7', '9'], words = ['three', 'seven', 'nine'];
        var Ctx = root.AudioContext || root.webkitAudioContext;
        if (!Ctx) return Promise.resolve(null);
        var ctx;
        try { ctx = new Ctx({ sampleRate: SR }); } catch (_) { return Promise.resolve(null); }
        return Promise.all(names.map(function (n) {
            return fetch((base || '/audio/digits/en/') + n + '.mp3').then(function (r) {
                if (!r.ok) throw new Error('kat-' + r.status);
                return r.arrayBuffer();
            }).then(function (ab) { return ctx.decodeAudioData(ab); });
        })).then(function (bufs) {
            var total = 0, windows = [], at = 0;
            bufs.forEach(function (b) { total += b.length; });
            var pcm = new Float32Array(total);
            bufs.forEach(function (b) {
                pcm.set(b.getChannelData(0), at);
                windows.push([at / b.sampleRate, (at + b.length) / b.sampleRate]);
                at += b.length;
            });
            try { ctx.close(); } catch (_) {}
            // decodeAudioData resamples to the context's rate; if the browser
            // refused 16 kHz the clip is unusable as a KAT — skip the GPU rung.
            if (Math.abs(bufs[0].sampleRate - SR) > 1) return null;
            return { pcm: pcm, text: words.join(' '), ipa: KAT_IPA.slice(), windows: windows };
        }).catch(function () { try { ctx.close(); } catch (_) {} return null; });
    }

    function workerUrl(provider) {
        var base = (root.__lyricsWorkerBase && root.__lyricsWorkerBase[provider]) ||
            (provider === 'gigaam' ? '/js/gigaam-worker.js' : '/js/phoneme-align-worker.js');
        var loc = root.__LOCALE || 'en';
        var url = base + (base.indexOf('?') >= 0 ? '&' : '?') + 'locale=' + encodeURIComponent(loc);
        /* GigaAM's worker loads ORT itself: which build is the owner's answer, carried
           on the worker's URL (see gigaam-worker.js). The phoneme lane runs inside
           transformers.js, which brings its own runtime. */
        var B = root.__ortBuild;
        return (provider === 'gigaam' && B && B.workerUrl) ? B.workerUrl(url, { simdOnly: true }) : url;
    }

    function fail(code, extra) {
        var e = new Error(code); e.code = code;
        if (extra) for (var k in extra) e[k] = extra[k];
        return e;
    }

    /* ── The words' phonemes, from our server ─────────────────────────────────
       espeak-ng (GPL-3) runs on the server as a program and is never shipped;
       the page asks once per LANGUAGE for the song's unique words. The page may
       come from PageCache with a stale CSRF token, so the token is readied
       first (__csrfReady), and the global fetch wrapper retries a 419 once. */
    function serverG2p(lang, words) {
        var ready = (root.__csrfReady ? root.__csrfReady() : Promise.resolve(null));
        return ready.then(function (tk) {
            var meta = root.document && root.document.querySelector('meta[name="csrf-token"]');
            var token = tk || (meta && meta.content) || '';
            var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
            var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (_) {} }, 20000) : null;
            return fetch(G2P_URL, {
                method: 'POST', credentials: 'same-origin', signal: ac ? ac.signal : undefined,
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json',
                           'X-Requested-With': 'XMLHttpRequest', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ lang: lang, words: words }),
            }).then(function (r) {
                if (timer) clearTimeout(timer);
                if (!r.ok) throw new Error('g2p-' + r.status);
                return r.json();
            }).then(function (d) {
                if (!d || !Array.isArray(d.ipa) || d.ipa.length !== words.length) throw new Error('g2p-shape');
                return d.ipa;
            });
        });
    }

    /* prepared.words[i] in language prepared.langs[i] → IPA per model word. */
    function phonemesFor(prepared, g2p) {
        var ask = g2p || serverG2p;
        var byLang = {}, order = [];
        for (var i = 0; i < prepared.words.length; i++) {
            var L = prepared.langs[i] || 'en';
            if (!byLang[L]) { byLang[L] = { index: {}, list: [] }; order.push(L); }
            var w = prepared.words[i];
            if (!(w in byLang[L].index)) { byLang[L].index[w] = byLang[L].list.length; byLang[L].list.push(w); }
        }
        return Promise.all(order.map(function (L) {
            return ask(L, byLang[L].list).then(function (ipa) { byLang[L].ipa = ipa; });
        })).then(function () {
            return prepared.words.map(function (w, i) {
                var g = byLang[prepared.langs[i] || 'en'];
                return String(g.ipa[g.index[w]] || '');
            });
        });
    }

    /* ── The model, before the voice exists ───────────────────────────────────
     * Placing words needs two things that are ready at different times: the
     * MODEL (a one-time download, 305 MB for Russian) and the VOICE (the vocal
     * stem, minutes of separation away). Until 30.09.2026 the download could
     * only start after the separation, when the person came back to press the
     * button: RU 30 days, a cold GigaAM run took 350–430 s against 30–60 s warm,
     * all of it after the separation. prefetch() puts the model into the
     * worker's cache NOW (the page calls it when the person agrees while the
     * voice is still being split); run() later finds it there.
     *
     * GigaAM only: its worker owns its cache and its URLs, so «download, do not
     * build a session» is one message. The phoneme lane loads through
     * transformers.js, whose cache keys belong to the library — filling them
     * from outside would be a guess, and a wrong guess downloads twice; it is 7
     * of 208 karaoke offers (RU, 30 days). For it prefetch() resolves at once
     * and run() downloads as before.
     *   → Promise<{ skipped:boolean }> · rejects with err.code 'aborted' | 'model-failed' | 'no-worker' */
    function prefetch(provider, opts) {
        opts = opts || {};
        if (provider !== 'gigaam' || typeof Worker === 'undefined') return Promise.resolve({ skipped: true });
        var onProgress = opts.onProgress || function () {};
        return new Promise(function (resolve, reject) {
            var w, settled = false;
            try { w = new Worker(workerUrl('gigaam')); } catch (e) { reject(fail('no-worker')); return; }
            function done(fn, arg) { if (settled) return; settled = true; try { w.terminate(); } catch (_) {} fn(arg); }
            if (opts.signal) {
                if (opts.signal.aborted) { done(reject, fail('aborted')); return; }
                opts.signal.addEventListener('abort', function () { done(reject, fail('aborted')); }, { once: true });
            }
            w.onerror = function (ev) { done(reject, fail('model-failed', { detail: (ev && ev.message) || '' })); };
            w.onmessage = function (e) {
                var d = e.data || {};
                if (d.type === 'progress' && d.phase === 'model' && d.total) onProgress({ phase: 'download', loaded: d.loaded, total: d.total });
                else if (d.type === 'prefetched') done(resolve, { skipped: false });
                else if (d.type === 'error') done(reject, fail('model-failed', { detail: d.message || d.error || '' }));
            };
            w.postMessage({ type: 'prefetch', locale: (root.__LOCALE || 'en') });
        });
    }

    /**
     * run({ lines, vocalBuffer, mixBuffer, durationS, signal, onProgress, katBase, langHint, g2p })
     *   → { timeline, provider, lang, device, score, medianConf, mismatch, gated, vocalShare, ms }
     * Rejects with err.code: 'no-text' | 'script-unsupported' | 'no-vocals' |
     *   'no-worker' | 'g2p-failed' | 'model-failed' | 'align-failed' | 'aborted'
     */
    function run(opts) {
        opts = opts || {};
        var onProgress = opts.onProgress || function () {};
        var lines = (opts.lines || []).map(function (l) { return String(l == null ? '' : l).trim(); }).filter(Boolean);
        var plan = planFor(lines.join('\n'), opts.langHint || root.__LOCALE);
        if (!plan.provider) return Promise.reject(fail(plan.reason, { script: plan.script }));
        if (!opts.vocalBuffer || !opts.mixBuffer) return Promise.reject(fail('no-vocals', { share: 0 }));
        if (typeof Worker === 'undefined') return Promise.reject(fail('no-worker'));
        var prepared = prepareWords(lines, plan.provider, plan);
        if (!prepared.count) return Promise.reject(fail('no-text'));
        var t0 = Date.now(), durationS = opts.durationS || opts.mixBuffer.duration;
        var phon = plan.provider === 'phon';

        onProgress({ phase: 'prepare' });
        var ipaP = phon
            ? phonemesFor(prepared, opts.g2p).catch(function (e) { throw fail('g2p-failed', { detail: String((e && e.message) || e) }); })
            : Promise.resolve(null);
        return Promise.all([
            toMono16k(opts.vocalBuffer, null, 0),
            toMono16k(opts.mixBuffer, null, 0),
            ipaP,
        ]).then(function (pcm) {
            var voc = pcm[0], mix = pcm[1], ipa = pcm[2];
            /* trustStem:false — the "vocal" came from centre-cut or CascadedNet
               and carries the snare and the bass. It can neither gate nor tell a
               backing track from a song, so the aligner gets the plain mix: the
               lane /audio-to-video runs, not the stem lane. Said in telemetry as
               gated:0. */
            var trust = opts.trustStem !== false;
            var share = trust ? vocalShare(voc, mix, SR) : null;
            if (trust && share < VOCAL_MIN_SHARE) throw fail('no-vocals', { share: share });
            var act = (trust && root.VocalActivity) ? root.VocalActivity.segments(voc, SR) : { segments: null, coverage: 1 };
            var input;
            if (trust) {
                input = new Float32Array(voc.length);
                for (var i = 0; i < voc.length; i++) input[i] = voc[i] + BLEND * (mix[i] || 0);
            } else {
                input = mix;
            }
            var mismatchAt = !phon ? TEXT_MISMATCH_CONF : (trust ? TEXT_MISMATCH_CONF_STEM : TEXT_MISMATCH_CONF_MIX);

            /* One worker, one attempt. `kat` present = the GPU rung may be tried
               (behind its known answer); absent = WASM only. */
            function attempt(kat) {
                return new Promise(function (resolve, reject) {
                    var w, settled = false, device = plan.provider === 'gigaam' ? 'wasm' : null;
                    var wUrl = workerUrl(plan.provider), onLegacy = /[?&]ortb=/.test(wUrl);
                    try { w = new Worker(wUrl); } catch (e) { reject(fail('no-worker')); return; }
                    function done(fn, arg) { if (settled) return; settled = true; try { w.terminate(); } catch (_) {} fn(arg); }
                    if (opts.signal) {
                        if (opts.signal.aborted) { done(reject, fail('aborted')); return; }
                        opts.signal.addEventListener('abort', function () { done(reject, fail('aborted')); }, { once: true });
                    }
                    w.onerror = function (ev) { done(reject, fail('model-failed', { detail: (ev && ev.message) || '' })); };
                    w.onmessage = function (e) {
                        var d = e.data || {};
                        if (d.type === 'progress') {
                            if (d.phase === 'model' && d.total) onProgress({ phase: 'download', loaded: d.loaded, total: d.total });
                            else if (d.phase === 'session') onProgress({ phase: 'session' });
                            else if (d.phase === 'chunk') onProgress({ phase: 'align', loaded: d.loaded, total: d.total });
                        } else if (d.type === 'verdict') {
                            rememberGpu(d.verdict && d.verdict.ok, d.verdict && d.verdict.why);
                            onProgress({ phase: 'gpu_verdict', verdict: d.verdict });
                        } else if (d.type === 'ready') {
                            device = d.device || device || 'wasm';
                            onProgress({ phase: 'ready', device: device });
                        } else if (d.type === 'align_result') {
                            if (!d.words || d.words.length !== prepared.count) {
                                done(reject, fail('align-failed', { detail: d.reason || 'word-count' })); return;
                            }
                            var built = timelineFromWords(lines, prepared, d.words, durationS);
                            done(resolve, {
                                timeline: built.timeline, provider: plan.provider, lang: plan.lang || null,
                                device: d.device || device || 'wasm',
                                score: d.score, medianConf: built.medianConf,
                                mismatch: built.medianConf != null && built.medianConf < mismatchAt,
                                gated: !!d.gated, vocalShare: share, coverage: act.coverage, ms: Date.now() - t0,
                            });
                        } else if (d.type === 'error') {
                            done(reject, fail('model-failed', { detail: d.message || d.error || '', device: device, ortLegacy: onLegacy }));
                        }
                    };
                    var norm = trust ? 'track' : 'chunk';
                    var align = phon
                        ? { type: 'align', pcm: input, words: ipa, norm: norm, active: act.segments }
                        : { type: 'align', pcm: input, text: prepared.text, romanized: true, norm: norm, active: act.segments };
                    if (plan.provider === 'gigaam') {
                        w.postMessage({ type: 'init', locale: (root.__LOCALE || 'en') });
                    } else {
                        w.postMessage({ type: 'init', rungs: (kat ? ['webgpu', 'wasm'] : ['wasm']), kat: kat });
                    }
                    w.postMessage(align);
                });
            }

            return (phon ? gpuUsable() : Promise.resolve(false)).then(function (wantGpu) {
                return wantGpu ? buildKat(opts.katBase) : null;
            }).then(function (kat) {
                return attempt(kat).catch(function (e) {
                    /* GigaAM could not start because the browser cannot RESERVE the
                       shared 4 GiB the current ORT build imports (WebKit before 18 —
                       «… RangeError: Out of memory»): the owner marks the browser and
                       answers ORT 1.18; once more in a fresh worker, after a pause. */
                    var B = root.__ortBuild;
                    if (plan.provider === 'gigaam' && e && e.code === 'model-failed' && !(opts.signal && opts.signal.aborted)
                        && B && B.initFailed && B.initFailed(String(e.detail || ''), { legacy: e.ortLegacy ? 'legacy' : '', from: 'karaoke' })) {
                        return new Promise(function (r) { setTimeout(r, 1000); }).then(function () { return attempt(null); });
                    }
                    /* A GPU that THREW — while loading, or in the middle of the
                       song after its known answer passed — takes the WASM rung of
                       the same worker down with it: measured 24.09.2026, a WebGPU
                       rung that could not get an adapter left the WASM rung failing
                       with the same «no available backend found». (A GPU that
                       answered WRONGLY is different: its known answer refused it
                       and the same worker went on to WASM.) One retry, in a fresh
                       worker, on WASM alone. */
                    if (!kat || !e || e.code !== 'model-failed' || (opts.signal && opts.signal.aborted)) throw e;
                    onProgress({ phase: 'gpu_fallback', detail: String(e.detail || '').slice(0, 120) });
                    return attempt(null).then(function (res) { res.gpuFallback = true; return res; });
                });
            });
        });
    }

    return {
        run: run, prefetch: prefetch, planFor: planFor, scriptOf: scriptOf, prepareWords: prepareWords, modelWord: modelWord,
        vocalShare: vocalShare, timelineFromWords: timelineFromWords, estimate: estimate,
        modelCached: modelCached, hasWebGpu: hasWebGpu, gpuUsable: gpuUsable, _rememberGpu: rememberGpu, MODEL_MB: MODEL_MB,
        BLEND: BLEND, VOCAL_MIN_SHARE: VOCAL_MIN_SHARE, TEXT_MISMATCH_CONF: TEXT_MISMATCH_CONF,
        TEXT_MISMATCH_CONF_STEM: TEXT_MISMATCH_CONF_STEM, TEXT_MISMATCH_CONF_MIX: TEXT_MISMATCH_CONF_MIX,
        PHON_LANGS: PHON_LANGS, KAT_IPA: KAT_IPA,
        phonemesFor: phonemesFor,
    };
});
